import React from "react";
import api, { MEDIA } from "../../lib/api";
import { toast } from "sonner";
import {
    Search, AlertTriangle, Star, Package as PkgIcon,
    History, X, ArrowUp, ArrowDown, ArrowLeftRight,
    Plus, Pencil, Trash2, MapPin, Check,
} from "lucide-react";

const MOVEMENT_LABELS = {
    sale: "Vânzare",
    return: "Retur",
    manual_adjustment: "Ajustare manuală",
    receiving: "Recepție marfă",
    adjustment: "Ajustare",
    location_transfer: "Transfer locație",
    location_set: "Setare locație",
};

// ─────────────────────────────────────────────────────────────────────────────
export default function AdminInventory() {
    const [tab, setTab] = React.useState("inventory"); // inventory | bylocation | manage

    const [items,        setItems]       = React.useState([]);
    const [q,            setQ]           = React.useState("");
    const [onlyCritical, setOnlyCritical]= React.useState(false);
    const [onlyLow,      setOnlyLow]     = React.useState(false);
    const [timer,        setTimer]       = React.useState(null);
    const [suppliers,    setSuppliers]   = React.useState([]);
    const [movements,    setMovements]   = React.useState(null);
    const [transferModal,setTransferModal]= React.useState(null);

    const [locations,    setLocations]   = React.useState([]);

    // ── Load data ─────────────────────────────────────────────────────────────
    const loadLocations = React.useCallback(async () => {
        try { const r = await api.get("/admin/stock-locations"); setLocations(r.data || []); }
        catch {}
    }, []);

    const loadItems = React.useCallback(async () => {
        const p = new URLSearchParams();
        if (q)            p.set("q", q);
        if (onlyCritical) p.set("only_critical", "true");
        if (onlyLow)      p.set("only_low", "true");
        const r = await api.get("/admin/inventory/search?" + p.toString());
        setItems(r.data || []);
    }, [q, onlyCritical, onlyLow]);

    React.useEffect(() => {
        if (timer) clearTimeout(timer);
        const t = setTimeout(loadItems, 250);
        setTimer(t);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, onlyCritical, onlyLow]);

    React.useEffect(() => {
        api.get("/admin/suppliers").then(r => setSuppliers(r.data || [])).catch(() => {});
        loadLocations();
    }, [loadLocations]);

    // ── Product patches ───────────────────────────────────────────────────────
    const patch = async (id, body) => {
        await api.put(`/admin/inventory/${id}`, body);
        setItems(prev => prev.map(p => p.product_id === id ? { ...p, ...body } : p));
    };

    // Inline set qty at a location (doesn't change total stock)
    const setLocQty = async (product_id, location_id, qty) => {
        try {
            await api.put(`/admin/location-stock/${product_id}/${location_id}`, { qty: parseInt(qty) || 0 });
            setItems(prev => prev.map(p => {
                if (p.product_id !== product_id) return p;
                return { ...p, stock_locations: { ...(p.stock_locations || {}), [location_id]: parseInt(qty) || 0 } };
            }));
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };

    const openMovements = async (p) => {
        const r = await api.get("/admin/inventory/movements", { params: { product_id: p.product_id, limit: 50 } });
        setMovements({ product: p, items: r.data || [] });
    };

    const criticalZero = items.filter(p => p.is_critical && (p.stock || 0) <= 0);

    // ── Tab labels ────────────────────────────────────────────────────────────
    const TABS = [
        { id: "inventory",   label: "Inventar produse" },
        { id: "bylocation",  label: "Stoc per locație" },
        { id: "manage",      label: "Gestionează locații" },
    ];

    return (
        <div data-testid="admin-inventory">
            <div className="overline mb-1">Administrare</div>
            <h1 className="font-heading text-3xl mb-4">Inventar & Locații stoc</h1>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-border mb-6">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px
                            ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        {t.label}
                    </button>
                ))}
                {locations.length === 0 && (
                    <span className="ml-auto self-center text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Nicio locație definită — adaugă în tab-ul „Gestionează locații"
                    </span>
                )}
            </div>

            {/* ══ TAB 1: Inventar produse ══════════════════════════════════════ */}
            {tab === "inventory" && (
                <InventoryTab
                    items={items} q={q} setQ={setQ}
                    onlyCritical={onlyCritical} setOnlyCritical={setOnlyCritical}
                    onlyLow={onlyLow} setOnlyLow={setOnlyLow}
                    suppliers={suppliers} locations={locations}
                    criticalZero={criticalZero}
                    patch={patch} setLocQty={setLocQty}
                    onTransfer={p => setTransferModal(p)}
                    onHistory={openMovements}
                />
            )}

            {/* ══ TAB 2: Stoc per locație ══════════════════════════════════════ */}
            {tab === "bylocation" && (
                <ByLocationTab items={items} locations={locations} />
            )}

            {/* ══ TAB 3: Gestionează locații ═══════════════════════════════════ */}
            {tab === "manage" && (
                <ManageLocationsTab
                    locations={locations}
                    items={items}
                    onChanged={() => { loadLocations(); loadItems(); }}
                />
            )}

            {/* Transfer modal */}
            {transferModal && (
                <TransferModal
                    product={transferModal}
                    locations={locations}
                    onClose={() => setTransferModal(null)}
                    onDone={() => { setTransferModal(null); loadItems(); }}
                />
            )}

            {/* Movements modal */}
            {movements && (
                <MovementsModal data={movements} onClose={() => setMovements(null)} />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1 — Inventar produse
// ─────────────────────────────────────────────────────────────────────────────
function InventoryTab({ items, q, setQ, onlyCritical, setOnlyCritical, onlyLow, setOnlyLow,
                        suppliers, locations, criticalZero, patch, setLocQty, onTransfer, onHistory }) {
    return (
        <>
            {criticalZero.length > 0 && (
                <div className="card-soft p-4 mb-4 border-destructive/40 bg-destructive/5">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                        <div>
                            <div className="font-medium">{criticalZero.length} produse importante cu stoc 0</div>
                            <div className="text-sm text-muted-foreground mt-1">
                                {criticalZero.slice(0, 5).map(p => p.name).join(", ")}
                                {criticalZero.length > 5 && ` +${criticalZero.length - 5}`}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="card-soft p-3 mb-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <input value={q} onChange={e => setQ(e.target.value)}
                        placeholder="Caută după nume sau cod..."
                        className="input-soft flex-1 !py-2" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={onlyCritical} onChange={e => setOnlyCritical(e.target.checked)} />
                    Doar importante
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={onlyLow} onChange={e => setOnlyLow(e.target.checked)} />
                    Doar stoc redus
                </label>
                <div className="text-xs text-muted-foreground ml-auto">{items.length} produse</div>
            </div>

            <div className="card-soft overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted">
                        <tr>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground w-12"></th>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">Produs</th>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground whitespace-nowrap">Stoc total</th>
                            {/* One column per location */}
                            {locations.map(loc => (
                                <th key={loc.location_id} className="text-left px-3 py-3 text-xs uppercase text-muted-foreground whitespace-nowrap">
                                    <span className="flex items-center gap-1">
                                        <MapPin className="h-3 w-3 text-primary" />
                                        {loc.name}
                                    </span>
                                </th>
                            ))}
                            {locations.length > 0 && (
                                <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground whitespace-nowrap">Nealocate</th>
                            )}
                            <th className="hidden md:table-cell text-left px-3 py-3 text-xs uppercase text-muted-foreground whitespace-nowrap">Furnizor stoc</th>
                            <th className="hidden md:table-cell text-left px-3 py-3 text-xs uppercase text-muted-foreground">Prag</th>
                            <th className="hidden md:table-cell text-left px-3 py-3 text-xs uppercase text-muted-foreground">Preț acq.</th>
                            <th className="hidden md:table-cell text-left px-3 py-3 text-xs uppercase text-muted-foreground">Furnizor</th>
                            <th className="hidden md:table-cell text-left px-3 py-3 text-xs uppercase text-muted-foreground">Flags</th>
                            <th className="px-3 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={10 + locations.length} className="text-center py-8 text-muted-foreground">Niciun produs.</td></tr>
                        )}
                        {items.map((p, i) => {
                            const isCritZero = p.is_critical && (p.stock || 0) <= 0;
                            const isLow = !isCritZero && (p.stock ?? 0) > 0 && (p.stock ?? 0) <= (p.low_stock_threshold ?? 5);
                            const locs = p.stock_locations || {};
                            const allocated = Object.values(locs).reduce((a, b) => a + (b || 0), 0);
                            const unallocated = Math.max(0, (p.stock || 0) - allocated);

                            return (
                                <tr key={p.product_id}
                                    className={`border-t border-border ${isCritZero ? "bg-destructive/5" : isLow ? "bg-amber-50/60" : ""}`}>
                                    <td className="px-3 py-2">
                                        {p.images?.[0]
                                            ? <img src={MEDIA(p.images[0])} alt="" className="w-10 h-10 object-cover rounded-lg" />
                                            : <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center"><PkgIcon className="h-4 w-4 text-muted-foreground" /></div>}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium max-w-[120px] sm:max-w-[200px] truncate">{p.name}</div>
                                        <div className="text-xs font-mono text-muted-foreground">{p.code}</div>
                                    </td>
                                    {/* Total stock */}
                                    <td className="px-3 py-2">
                                        <input type="number" defaultValue={p.stock ?? 0}
                                            onBlur={e => patch(p.product_id, { stock: Number(e.target.value) })}
                                            className={`input-soft !py-1 w-20 text-sm font-mono ${isCritZero ? "border-destructive" : isLow ? "border-amber-400" : ""}`} />
                                    </td>
                                    {/* Location columns — editable */}
                                    {locations.map(loc => (
                                        <td key={loc.location_id} className="px-3 py-2">
                                            <input
                                                type="number"
                                                defaultValue={locs[loc.location_id] ?? 0}
                                                key={`${p.product_id}-${loc.location_id}-${locs[loc.location_id]}`}
                                                onBlur={e => setLocQty(p.product_id, loc.location_id, e.target.value)}
                                                className={`input-soft !py-1 w-20 text-sm font-mono
                                                    ${(locs[loc.location_id] || 0) > 0 ? "border-primary/40 bg-primary/5" : ""}`}
                                                title={`${p.name} la ${loc.name}`}
                                            />
                                        </td>
                                    ))}
                                    {/* Unallocated */}
                                    {locations.length > 0 && (
                                        <td className="px-3 py-2">
                                            <span className={`font-mono text-sm px-2 py-0.5 rounded
                                                ${unallocated === 0 ? "text-emerald-700 bg-emerald-50" : "text-amber-700 bg-amber-50"}`}>
                                                {unallocated}
                                            </span>
                                        </td>
                                    )}
                                    {/* Rest of columns — hidden on mobile */}
                                    <td className="hidden md:table-cell px-3 py-2">
                                        <input type="number" defaultValue={p.stock_supplier ?? 0}
                                            onBlur={e => patch(p.product_id, { stock_supplier: Number(e.target.value) })}
                                            className="input-soft !py-1 w-20 text-sm" />
                                    </td>
                                    <td className="hidden md:table-cell px-3 py-2">
                                        <input type="number" defaultValue={p.low_stock_threshold ?? 5}
                                            onBlur={e => patch(p.product_id, { low_stock_threshold: Number(e.target.value) })}
                                            className="input-soft !py-1 w-16 text-sm" />
                                    </td>
                                    <td className="hidden md:table-cell px-3 py-2">
                                        <input type="number" step="0.01" defaultValue={p.purchase_price ?? ""}
                                            onBlur={e => patch(p.product_id, { purchase_price: e.target.value ? Number(e.target.value) : null })}
                                            placeholder="0.00" className="input-soft !py-1 w-24 text-sm" />
                                    </td>
                                    <td className="hidden md:table-cell px-3 py-2">
                                        <select defaultValue={p.supplier_id || ""}
                                            onChange={e => patch(p.product_id, { supplier_id: e.target.value || null })}
                                            className="input-soft !py-1 text-xs max-w-[120px]">
                                            <option value="">—</option>
                                            {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
                                        </select>
                                    </td>
                                    <td className="hidden md:table-cell px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <button type="button" onClick={() => patch(p.product_id, { is_critical: !p.is_critical })} title="Important">
                                                <Star className={`h-4 w-4 ${p.is_critical ? "text-amber-500 fill-amber-500" : "text-muted-foreground"}`} />
                                            </button>
                                            <input type="checkbox" checked={!!p.manual_out_of_stock}
                                                onChange={e => patch(p.product_id, { manual_out_of_stock: e.target.checked })}
                                                title="Forțat epuizat" />
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-1">
                                            {locations.length >= 2 && (
                                                <button onClick={() => onTransfer(p)} title="Mută stoc între locații"
                                                    className="btn-ghost p-1.5 text-primary">
                                                    <ArrowLeftRight className="h-4 w-4" />
                                                </button>
                                            )}
                                            <button onClick={() => onHistory(p)} title="Istoric mișcări"
                                                className="btn-ghost p-1.5 text-muted-foreground">
                                                <History className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {locations.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                    <span className="font-medium">Cum funcționează coloanele de locații:</span>{" "}
                    editezi direct numărul din celulă și dai Enter/click în altă parte pentru a salva.
                    Stocul total nu se modifică — doar distribui unde se află produsul fizic.
                    Folosește butonul <ArrowLeftRight className="h-3 w-3 inline" /> pentru a muta cantitate dintr-o locație în alta.
                </p>
            )}
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2 — Stoc per locație
// ─────────────────────────────────────────────────────────────────────────────
function ByLocationTab({ items, locations }) {
    const [selectedLoc, setSelectedLoc] = React.useState(null);

    React.useEffect(() => {
        if (locations.length > 0 && !selectedLoc) setSelectedLoc(locations[0].location_id);
    }, [locations, selectedLoc]);

    if (locations.length === 0) {
        return (
            <div className="card-soft p-8 text-center text-muted-foreground">
                <MapPin className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Nicio locație definită</p>
                <p className="text-sm mt-1">Mergi la tab-ul „Gestionează locații" pentru a adăuga prima locație.</p>
            </div>
        );
    }

    const loc = locations.find(l => l.location_id === selectedLoc);
    const productsHere = items
        .map(p => ({ ...p, qty: (p.stock_locations || {})[selectedLoc] || 0 }))
        .filter(p => p.qty > 0)
        .sort((a, b) => b.qty - a.qty);

    const totalQty = productsHere.reduce((s, p) => s + p.qty, 0);

    return (
        <div className="space-y-4">
            {/* Location selector */}
            <div className="flex flex-wrap gap-2">
                {locations.map(l => {
                    const cnt = items.filter(p => ((p.stock_locations || {})[l.location_id] || 0) > 0).length;
                    const total = items.reduce((s, p) => s + (((p.stock_locations || {})[l.location_id]) || 0), 0);
                    return (
                        <button key={l.location_id}
                            onClick={() => setSelectedLoc(l.location_id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors
                                ${selectedLoc === l.location_id
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "border-border hover:border-primary/40 hover:bg-muted"}`}>
                            <MapPin className="h-4 w-4" />
                            <span>{l.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-mono
                                ${selectedLoc === l.location_id ? "bg-white/20" : "bg-muted"}`}>
                                {total} buc · {cnt} prod.
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Products at this location */}
            {loc && (
                <div className="card-soft overflow-hidden">
                    <div className="bg-muted px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2 font-medium">
                            <MapPin className="h-4 w-4 text-primary" />
                            {loc.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                            <span className="font-mono font-bold text-foreground">{totalQty}</span> bucăți totale ·{" "}
                            <span className="font-mono font-bold text-foreground">{productsHere.length}</span> produse
                        </div>
                    </div>

                    {productsHere.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            <PkgIcon className="h-8 w-8 mx-auto mb-2 opacity-30" />
                            <p>Niciun produs alocat la <strong>{loc.name}</strong>.</p>
                            <p className="text-xs mt-1">Mergi la tab-ul „Inventar produse" și completează coloana „{loc.name}" pentru fiecare produs.</p>
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border">
                                    <th className="text-left px-4 py-2 text-xs uppercase text-muted-foreground w-12"></th>
                                    <th className="text-left px-4 py-2 text-xs uppercase text-muted-foreground">Produs</th>
                                    <th className="hidden sm:table-cell text-left px-4 py-2 text-xs uppercase text-muted-foreground">Cod</th>
                                    <th className="text-right px-4 py-2 text-xs uppercase text-muted-foreground">Qty locație</th>
                                    <th className="text-right px-4 py-2 text-xs uppercase text-muted-foreground">Total</th>
                                    <th className="hidden sm:table-cell text-right px-4 py-2 text-xs uppercase text-muted-foreground">% din total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productsHere.map((p, i) => (
                                    <tr key={p.product_id} className="border-t border-border hover:bg-muted/30">
                                        <td className="px-4 py-2">
                                            {p.images?.[0]
                                                ? <img src={MEDIA(p.images[0])} alt="" className="w-9 h-9 object-cover rounded-lg" />
                                                : <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center"><PkgIcon className="h-3.5 w-3.5 text-muted-foreground" /></div>}
                                        </td>
                                        <td className="px-4 py-2 font-medium max-w-[160px] truncate">{p.name}</td>
                                        <td className="hidden sm:table-cell px-4 py-2 font-mono text-xs text-muted-foreground">{p.code}</td>
                                        <td className="px-4 py-2 text-right">
                                            <span className="font-mono font-bold text-lg text-primary">{p.qty}</span>
                                            <span className="text-muted-foreground text-xs ml-1">buc</span>
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono text-muted-foreground">{p.stock ?? 0}</td>
                                        <td className="hidden sm:table-cell px-4 py-2 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <div className="w-16 bg-muted rounded-full h-1.5">
                                                    <div className="bg-primary h-1.5 rounded-full"
                                                         style={{ width: `${Math.min(100, (p.qty / (p.stock || 1)) * 100)}%` }} />
                                                </div>
                                                <span className="text-xs text-muted-foreground w-8 text-right">
                                                    {Math.round((p.qty / (p.stock || 1)) * 100)}%
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3 — Gestionează locații
// ─────────────────────────────────────────────────────────────────────────────
function ManageLocationsTab({ locations, items, onChanged }) {
    const [newName,  setNewName]  = React.useState("");
    const [editId,   setEditId]   = React.useState(null);
    const [editName, setEditName] = React.useState("");

    const add = async () => {
        if (!newName.trim()) return;
        try {
            await api.post("/admin/stock-locations", { name: newName.trim() });
            setNewName("");
            toast.success("Locație adăugată");
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };

    const save = async (loc_id) => {
        if (!editName.trim()) return;
        try {
            await api.put(`/admin/stock-locations/${loc_id}`, { name: editName.trim() });
            setEditId(null);
            toast.success("Redenumit");
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };

    const del = async (loc) => {
        const qtyTotal = items.reduce((s, p) => s + (((p.stock_locations || {})[loc.location_id]) || 0), 0);
        const msg = qtyTotal > 0
            ? `Locația "${loc.name}" are ${qtyTotal} bucăți alocate. La ștergere, alocarea va fi eliminată (stocul total nu se modifică). Continui?`
            : `Ștergi locația "${loc.name}"?`;
        if (!window.confirm(msg)) return;
        try {
            await api.delete(`/admin/stock-locations/${loc.location_id}`);
            toast.success("Locație ștearsă");
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };

    return (
        <div className="max-w-xl space-y-6">
            {/* Existing locations */}
            <div className="card-soft overflow-hidden">
                <div className="bg-muted px-4 py-3 font-medium text-sm">
                    Locații definite ({locations.length})
                </div>
                {locations.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground text-sm">
                        Nicio locație. Adaugă prima locație mai jos.
                    </div>
                ) : (
                    <div className="divide-y divide-border">
                        {locations.map(loc => {
                            const prodCount = items.filter(p => ((p.stock_locations || {})[loc.location_id] || 0) > 0).length;
                            const qtyTotal  = items.reduce((s, p) => s + (((p.stock_locations || {})[loc.location_id]) || 0), 0);
                            return (
                                <div key={loc.location_id} className="flex items-center gap-3 px-4 py-3">
                                    <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                                    <div className="flex-1">
                                        {editId === loc.location_id ? (
                                            <div className="flex items-center gap-2">
                                                <input value={editName}
                                                    onChange={e => setEditName(e.target.value)}
                                                    onKeyDown={e => { if (e.key === "Enter") save(loc.location_id); if (e.key === "Escape") setEditId(null); }}
                                                    className="input-soft !py-1 text-sm flex-1"
                                                    autoFocus />
                                                <button onClick={() => save(loc.location_id)} className="btn-primary !py-1 !px-2 text-xs flex items-center gap-1">
                                                    <Check className="h-3 w-3" /> OK
                                                </button>
                                                <button onClick={() => setEditId(null)} className="btn-ghost !py-1 !px-2 text-xs">
                                                    Anulează
                                                </button>
                                            </div>
                                        ) : (
                                            <div>
                                                <span className="font-medium">{loc.name}</span>
                                                <span className="text-xs text-muted-foreground ml-2">
                                                    {qtyTotal > 0
                                                        ? `${qtyTotal} buc · ${prodCount} produse`
                                                        : "fără stoc alocat"}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {editId !== loc.location_id && (
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => { setEditId(loc.location_id); setEditName(loc.name); }}
                                                className="btn-ghost p-1.5" title="Redenumește">
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            <button onClick={() => del(loc)}
                                                className="btn-ghost p-1.5 text-destructive" title="Șterge locație">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Add new */}
            <div className="card-soft p-4 space-y-3">
                <h3 className="font-medium text-sm">Adaugă locație nouă</h3>
                <p className="text-xs text-muted-foreground">
                    Exemple: <em>Depozit</em>, <em>Mașina Ion</em>, <em>Showroom</em>, <em>Service</em>, <em>Birou</em> — orice loc fizic unde se pot afla produse.
                </p>
                <div className="flex gap-2">
                    <input value={newName} onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && add()}
                        placeholder="Denumire locație..."
                        className="input-soft !py-2 text-sm flex-1" />
                    <button onClick={add} className="btn-primary flex items-center gap-1 !py-2">
                        <Plus className="h-4 w-4" /> Adaugă
                    </button>
                </div>
            </div>

            <div className="card-soft p-4 bg-muted/30 text-sm text-muted-foreground space-y-1.5">
                <p className="font-medium text-foreground">Cum funcționează:</p>
                <p>1. Adaugi locațiile tale (depozit, mașini, showroom, etc.)</p>
                <p>2. În tab-ul <strong>Inventar produse</strong> apare câte o coloană per locație — editezi direct cantitatea din celulă</p>
                <p>3. Stocul total al unui produs NU se schimbă când distribui pe locații — distribui din totalul existent</p>
                <p>4. Butonul <ArrowLeftRight className="h-3.5 w-3.5 inline" /> transferă cantitate dintr-o locație în alta (nu modifică totalul)</p>
                <p>5. Tab-ul <strong>Stoc per locație</strong> îți arată imediat ce se află la fiecare locație</p>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Transfer modal
// ─────────────────────────────────────────────────────────────────────────────
function TransferModal({ product, locations, onClose, onDone }) {
    const locs = product.stock_locations || {};
    const [fromLoc, setFromLoc] = React.useState(locations[0]?.location_id || "");
    const [toLoc,   setToLoc]   = React.useState(locations[1]?.location_id || "");
    const [qty,     setQty]     = React.useState(1);
    const [loading, setLoading] = React.useState(false);

    const fromQty   = locs[fromLoc] ?? 0;
    const toQty     = locs[toLoc]   ?? 0;
    const fromName  = locations.find(l => l.location_id === fromLoc)?.name || "—";
    const toName    = locations.find(l => l.location_id === toLoc)?.name   || "—";
    const qtyParsed = Math.max(0, parseInt(qty) || 0);
    const valid     = fromLoc !== toLoc && qtyParsed > 0 && qtyParsed <= fromQty;

    const doTransfer = async () => {
        if (!valid) return;
        setLoading(true);
        try {
            await api.post("/admin/location-stock/transfer", {
                product_id:       product.product_id,
                from_location_id: fromLoc,
                to_location_id:   toLoc,
                qty:              qtyParsed,
            });
            toast.success(`${qtyParsed} buc mutate: ${fromName} → ${toName}`);
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare la transfer");
        } finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
            <div className="bg-card rounded-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <h3 className="font-heading text-lg flex items-center gap-2">
                            <ArrowLeftRight className="h-5 w-5 text-primary" /> Transfer stoc
                        </h3>
                        <p className="text-sm text-muted-foreground truncate max-w-xs mt-0.5">{product.name}</p>
                    </div>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Snapshot: all locations for this product */}
                    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(locations.length, 3)}, 1fr)` }}>
                        {locations.map(loc => {
                            const q = locs[loc.location_id] ?? 0;
                            return (
                                <div key={loc.location_id}
                                     className={`p-3 rounded-lg border-2 text-center ${q > 0 ? "border-primary/30 bg-primary/5" : "border-border bg-muted/20"}`}>
                                    <div className="text-xs text-muted-foreground truncate">{loc.name}</div>
                                    <div className="font-mono font-bold text-2xl mt-1 leading-none">{q}</div>
                                    <div className="text-xs text-muted-foreground">buc</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Transfer form */}
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-muted-foreground block mb-1 font-medium">Din locație</label>
                                <select value={fromLoc} onChange={e => setFromLoc(e.target.value)}
                                    className="input-soft !py-2 text-sm w-full">
                                    {locations.map(l => (
                                        <option key={l.location_id} value={l.location_id}>{l.name} ({locs[l.location_id] ?? 0} buc)</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground block mb-1 font-medium">În locație</label>
                                <select value={toLoc} onChange={e => setToLoc(e.target.value)}
                                    className="input-soft !py-2 text-sm w-full">
                                    {locations.map(l => (
                                        <option key={l.location_id} value={l.location_id}>{l.name} ({locs[l.location_id] ?? 0} buc)</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs text-muted-foreground block mb-1 font-medium">
                                Cantitate de mutat
                                <span className="ml-1 text-muted-foreground font-normal">(disponibil în „{fromName}": <span className="font-mono font-bold">{fromQty}</span> buc)</span>
                            </label>
                            <input type="number" min="1" max={fromQty} value={qty}
                                onChange={e => setQty(e.target.value)}
                                className={`input-soft !py-2 text-sm w-full ${!valid && qtyParsed > 0 ? "border-destructive" : ""}`} />
                            {qtyParsed > fromQty && qtyParsed > 0 && (
                                <p className="text-xs text-destructive mt-1">Cantitate mai mare decât ce există în „{fromName}" ({fromQty} buc)</p>
                            )}
                            {fromLoc === toLoc && (
                                <p className="text-xs text-destructive mt-1">Sursa și destinația sunt identice</p>
                            )}
                        </div>

                        {/* Preview */}
                        {valid && (
                            <div className="flex items-center gap-3 bg-muted/50 rounded-lg px-4 py-3 text-sm">
                                <div className="text-center">
                                    <div className="text-xs text-muted-foreground">{fromName}</div>
                                    <div className="font-mono font-bold">{fromQty} <span className="text-muted-foreground text-xs">→</span> <span className="text-destructive">{fromQty - qtyParsed}</span></div>
                                </div>
                                <ArrowLeftRight className="h-4 w-4 text-primary flex-shrink-0" />
                                <div className="text-center">
                                    <div className="text-xs text-muted-foreground">{toName}</div>
                                    <div className="font-mono font-bold">{toQty} <span className="text-muted-foreground text-xs">→</span> <span className="text-emerald-600">{toQty + qtyParsed}</span></div>
                                </div>
                                <ArrowLeftRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mx-1" />
                                <div className="text-center">
                                    <div className="text-xs text-muted-foreground">Total produs</div>
                                    <div className="font-mono font-bold">{product.stock ?? 0} <span className="text-xs text-muted-foreground">(neschimbat)</span></div>
                                </div>
                            </div>
                        )}

                        <button onClick={doTransfer} disabled={loading || !valid}
                            className="btn-primary w-full flex items-center justify-center gap-2 !py-2.5">
                            {loading
                                ? "Se transferă..."
                                : <><ArrowLeftRight className="h-4 w-4" /> Mută {qtyParsed > 0 ? qtyParsed : ""} buc din {fromName} în {toName}</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Movements modal (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
function MovementsModal({ data, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
            <div className="bg-card rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <h3 className="font-heading text-xl">Istoric mișcări stoc</h3>
                        <p className="text-sm text-muted-foreground">{data.product.name}</p>
                    </div>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                    {data.items.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">Nicio mișcare înregistrată.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-border text-xs text-muted-foreground uppercase">
                                <th className="pb-2 text-left">Data</th>
                                <th className="pb-2 text-center">Tip</th>
                                <th className="pb-2 text-right">Modificare</th>
                                <th className="pb-2 text-right">Înainte</th>
                                <th className="pb-2 text-right">După</th>
                                <th className="pb-2 text-left">Motiv / Detalii</th>
                            </tr></thead>
                            <tbody>{data.items.map((m, i) => (
                                <tr key={i} className="border-b border-border/40 last:border-0">
                                    <td className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                                        {new Date(m.created_at).toLocaleString("ro-RO")}
                                    </td>
                                    <td className="py-2 text-center">
                                        <span className={`chip text-[10px] ${m.type === "location_transfer" ? "bg-primary/10 text-primary" : "bg-muted"}`}>
                                            {MOVEMENT_LABELS[m.type] || m.type}
                                        </span>
                                    </td>
                                    <td className="py-2 text-right font-mono font-medium">
                                        {m.type === "location_transfer" || m.type === "location_set" ? (
                                            <ArrowLeftRight className="h-3.5 w-3.5 text-primary inline" />
                                        ) : (
                                            <span className={`flex items-center justify-end gap-0.5 ${m.delta > 0 ? "text-emerald-600" : "text-destructive"}`}>
                                                {m.delta > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                                                {Math.abs(m.delta)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2 text-right font-mono">{m.qty_before ?? "—"}</td>
                                    <td className="py-2 text-right font-mono">{m.qty_after ?? "—"}</td>
                                    <td className="py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={m.reason}>{m.reason || "—"}</td>
                                </tr>
                            ))}</tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
